import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { RnSearch, RnX } from "@remi/icons/next";
import { isTruthy } from "@remi/ts-utils";

import { useEffect } from "../hooks/useEffect";
import { cn } from "../lib/utils";
import { getTestId } from "../utils/playwright";

const inputVariants = cva(
  "group relative inline-flex h-11 w-full min-w-0 items-center bg-background-primary px-2 font-normal text-text-primary focus-within:text-text-primary focus-within:ring-2 has-[:disabled]:bg-background-secondary [&>input]:placeholder:text-text-tertiary",
  {
    variants: {
      variant: {
        outlined: "rounded-md border",
        search: "w-full min-w-0 border-b focus-within:ring-0",
        invisible:
          "!h-fit !min-h-fit px-0 underline decoration-text-tertiary decoration-dotted underline-offset-2 focus-within:ring-0",
      },
      size: {
        xxs: "h-11 min-h-11 text-base md:h-6 md:min-h-6 md:w-12 md:min-w-0 md:text-xxs",
        xs: "h-11 min-h-11 text-base md:h-6 md:min-h-6 md:w-12 md:min-w-0 md:text-xs",
        sm: "h-11 min-h-11 text-base md:h-7 md:min-h-7 md:text-xs",
        md: "h-11 min-h-11 text-base md:h-9 md:min-h-9 md:text-xs",
        lg: "h-11 min-h-11 text-base md:text-xs",
      },
      state: {
        default: "border-border-primary",
        error: "border-border-destructive",
      },
      focus: {
        yes: "text-text-primary ring-2",
        no: "",
      },
    },
    compoundVariants: [
      {
        variant: "outlined",
        size: "xxs",
        class: "rounded-md",
      },
      {
        variant: "outlined",
        size: "xs",
        class: "rounded-md",
      },
      {
        variant: "outlined",
        state: "error",
        class: "focus-within:border-border-destructive focus-within:ring-border-destructiveLight",
      },
      {
        variant: "outlined",
        state: "default",
        class: "focus-within:border-border-primary focus-within:ring-border-tertiary",
      },
      {
        variant: "outlined",
        focus: "yes",
        state: "default",
        className: "border-border-primary ring-border-tertiary",
      },
      {
        variant: "outlined",
        focus: "yes",
        state: "error",
        className: "border-border-destructive ring-border-destructiveLight",
      },
    ],
    defaultVariants: {
      variant: "outlined",
      size: "md",
      focus: "no",
    },
  },
);

type RemiInputVariant = NonNullable<VariantProps<typeof inputVariants>["variant"]>;

const parseNumericLike = (candidate: React.InputHTMLAttributes<HTMLInputElement>["value"]): number | null => {
  if (candidate === undefined || candidate === "" || Array.isArray(candidate)) {
    return null;
  }
  const parsed = typeof candidate === "number" ? candidate : Number(candidate);
  return Number.isFinite(parsed) ? parsed : null;
};

const isZeroLike = (candidate: React.InputHTMLAttributes<HTMLInputElement>["value"]): boolean =>
  parseNumericLike(candidate) === 0;

export interface RemiInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "slot" | "onClick">,
    VariantProps<typeof inputVariants> {
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  error?: boolean;
  startSlot?: React.ReactNode;
  endSlot?: React.ReactNode;
  errorMessage?: string | React.ReactNode;
  helperMessage?: string | React.ReactNode;
  pseudoFocus?: boolean;
  onClear?: () => void;
  textAlign?: "right" | "left";
  /**
   * Only for fields where 0 and empty mean the same thing — it hides a real 0 from the user.
   * The input's value becomes empty, so a screen reader announces the placeholder instead: give
   * the field an explicit aria-label rather than anything that relies on the value reading "0".
   */
  zeroAsPlaceholder?: boolean;
  /** The caller recorded that this zero was typed, so it stays filled after the input remounts. */
  zeroTyped?: boolean;
  onClick?: (e: MouseEvent) => void;
  helperPosition?: "bottom-left" | "bottom-right" | "top-left" | "top-right";
  inputRef?: React.RefObject<HTMLInputElement | null>;
  ref?: React.Ref<HTMLDivElement>;
}

const RemiInput = ({
  ref,
  className,
  type,
  variant = "outlined",
  size = "md",
  error = false,
  startSlot,
  endSlot,
  errorMessage,
  helperMessage,
  icon,
  endIcon,
  pseudoFocus = false,
  placeholder = "Begin typing...",
  helperPosition = "bottom-left",
  onClear,
  onClick,
  inputRef,
  textAlign = "left",
  zeroAsPlaceholder = false,
  zeroTyped = false,
  value,
  onChange,
  onFocus,
  onBlur,
  ...props
}: RemiInputProps) => {
  const defaultRef = React.useRef<HTMLInputElement>(null);
  inputRef = inputRef ?? defaultRef;
  const dataTestId = getTestId(props) ?? "remi-input";
  const state = error ? "error" : "default";

  // Never rendered, only inspected: rendering it would swallow values set from outside the input.
  // Survives focus and blur on purpose so a typed zero stays filled after blur.
  const [typedValue, setTypedValue] = React.useState<string | null>(null);
  const [lastValue, setLastValue] = React.useState(value);

  // Stale only when the caller's value no longer matches what was typed — a round trip back to
  // the same number keeps the record instead of blanking it.
  if (zeroAsPlaceholder && !Object.is(value, lastValue)) {
    setLastValue(value);
    if (typedValue !== null && parseNumericLike(typedValue) !== parseNumericLike(value)) {
      setTypedValue(null);
    }
  }

  const typedAZero = typedValue !== null && isZeroLike(typedValue);
  const inputValue = zeroAsPlaceholder && !typedAZero && !zeroTyped && isZeroLike(value) ? "" : value;

  const valueHandlers = zeroAsPlaceholder
    ? {
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          setTypedValue(event.target.value);
          onChange?.(event);
        },
        onFocus,
        onBlur,
      }
    : { onChange, onFocus, onBlur };

  return (
    <div
      ref={ref}
      onClick={() => {
        inputRef.current?.focus();
        if (onClick) {
          onClick(new MouseEvent("click"));
        }
      }}
      className={cn(
        inputVariants({ variant, size, state, focus: pseudoFocus ? "yes" : "no" }),
        props.disabled === true && "cursor-default",
        className,
      )}
    >
      {isTruthy(startSlot) && (
        <div className="mr-2 ml-[-8px] h-full [&>button]:h-full [&>button]:rounded-s-[5px] [&>button]:rounded-e-none [&>button]:border-border-primary [&>button]:border-r">
          {startSlot}
        </div>
      )}
      {(isTruthy(icon) || variant === "search") && (
        <div className="mr-1 text-icon-tertiary">{variant === "search" ? <RnSearch size="sm" /> : icon}</div>
      )}
      <input
        type={type}
        ref={inputRef}
        placeholder={placeholder}
        data-testid={dataTestId}
        className={cn(
          "min-w-0 flex-1 truncate bg-transparent focus:outline-hidden disabled:bg-background-secondary disabled:text-text-tertiary",
          props.readOnly === true && "cursor-pointer",
          props.disabled === true && "cursor-default",
          textAlign === "right" && "text-right",
        )}
        {...props}
        value={inputValue}
        {...valueHandlers}
      />
      {(isTruthy(endIcon) || (onClear && inputValue !== "")) && (
        <div className={cn("ml-1.5 text-icon-tertiary", props.disabled === true ? "cursor-default" : "cursor-pointer")}>
          {onClear && inputValue !== "" ? <RnX data-testid="search-clear-icon" onClick={onClear} /> : endIcon}
        </div>
      )}
      {error && isTruthy(errorMessage) && (
        <span
          className="absolute top-full left-0 flex w-[90%] justify-start font-normal text-text-destructive text-xxs"
          data-testid={`${dataTestId}-error-message`}
        >
          {errorMessage}
        </span>
      )}
      {!error && isTruthy(helperMessage) && (
        <span
          className={cn(
            "absolute flex w-fit max-w-full justify-start text-[10px] text-text-tertiary",
            helperPosition === "bottom-left" && "top-full",
            helperPosition === "bottom-right" && "top-full right-0",
            helperPosition === "top-left" && "bottom-full",
            helperPosition === "top-right" && "right-0 bottom-full",
          )}
        >
          {helperMessage}
        </span>
      )}
      {isTruthy(endSlot) && (
        <div className="mr-[-8px] ml-2 h-full [&>button]:h-full [&>button]:rounded-s-none [&>button]:rounded-e-[5px] [&>button]:border-border-primary [&>button]:border-l">
          {endSlot}
        </div>
      )}
    </div>
  );
};
RemiInput.displayName = "RemiInput";

// this component holds internal state and then only updates the value onBlur
const RemiStatefulInput = ({
  ref,
  value,
  ...props
}: Omit<RemiInputProps, "onChange" | "zeroAsPlaceholder" | "zeroTyped">) => {
  const [val, setVal] = React.useState<string>(String(value));

  useEffect(() => {
    setVal(String(value));
  }, [value]);

  return <RemiInput ref={ref} value={val} onChange={(e) => setVal(e.target.value)} {...props} />;
};
RemiStatefulInput.displayName = "RemiStatefulInput";

export { RemiInput, type RemiInputVariant, RemiStatefulInput };
